// ============================================================
// D-291 — a save refused because the time overlaps another report of the
// same employee (409 WORK_LOG_OVERLAP). The server lists the overlapping
// reports and the end that would clear them by shortening (details.overlaps,
// details.suggested_end) — only to the employee whose time it is. Pure
// helpers for the shared notice (WorkLogOverlapNotice), no React.
// ============================================================

export interface OverlapItem {
  kind: 'log' | 'timer';
  id: string;
  ticket_id: string;
  ticket_title: string | null;
  ticket_number: number | null;
  task_type: string | null;
  start_time: string;
  /** null = a timer that is still running. */
  end_time: string | null;
}

/**
 * D-299 (QX-1302) - the times to send again, UNCHANGED, with the parallel
 * confirmation ("כן, עבדתי במקביל"). Null when the server did not echo them.
 */
export function parallelConfirmTimes(c: OverlapConflict): ShortenedTimes | null {
  if (!c.requestedStart || !c.requestedEnd) return null;
  return { start_time: c.requestedStart, end_time: c.requestedEnd, confirm_parallel: true };
}

/**
 * D-299 (QX-1302) - the exact preview of "לקצר אוטומטית": the SAME numbers the
 * server computed (details.suggested_end, start stays). Null = nothing to shorten.
 */
export function shortenPreview(
  c: OverlapConflict,
  opts: { wholeMinutes?: boolean } = {}
): { from: { start: string; end: string }; to: { start: string; end: string } } | null {
  if (!c.suggestedEnd || !c.requestedStart || !c.requestedEnd) return null;
  // A screen that sends a LENGTH in whole minutes (the documentation suggestions: start + floor(minutes))
  // saves exactly that - the preview must show the same end, not the server's raw suggested end.
  let end = c.suggestedEnd;
  if (opts.wholeMinutes) {
    const m = shortenedMinutes(c);
    if (!m) return null;
    end = new Date(new Date(c.requestedStart).getTime() + m * 60_000).toISOString();
  }
  return {
    from: { start: c.requestedStart, end: c.requestedEnd },
    to: { start: c.requestedStart, end },
  };
}

export interface OverlapConflict {
  /** The server's Hebrew sentence (kept for a screen reader / fallback). */
  message: string;
  overlaps: OverlapItem[];
  /** The end that clears every overlap (the start stays); null = shortening cannot help. */
  suggestedEnd: string | null;
  /** D-299: the server says the employee may confirm "I worked in parallel". */
  canConfirmParallel?: boolean;
  requestedStart: string | null;
  requestedEnd: string | null;
}

export const OVERLAP_ERROR_CODE = 'WORK_LOG_OVERLAP';

/** The structured overlap refusal, or null for any other error (or one without the list). */
export function overlapConflictOf(err: unknown): OverlapConflict | null {
  const e = err as { error?: { message?: string; error_code?: string; details?: Record<string, unknown> } } | null;
  if (!e || e.error?.error_code !== OVERLAP_ERROR_CODE) return null;
  const d = e.error.details ?? {};
  const list = Array.isArray(d.overlaps) ? (d.overlaps as OverlapItem[]).filter((o) => o && o.id && o.start_time) : [];
  if (list.length === 0) return null;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return {
    message: e.error.message ?? '',
    overlaps: list,
    suggestedEnd: str(d.suggested_end),
    canConfirmParallel: d.can_confirm_parallel === true,
    requestedStart: str(d.requested_start),
    requestedEnd: str(d.requested_end),
  };
}

/** "משימה" / "דיון" / "קריאה" — by the ticket's type. */
export function overlapKindKey(taskType: string | null | undefined): 'task' | 'discussion' | 'ticket' {
  if (taskType === 'discussion') return 'discussion';
  if (taskType === 'ticket') return 'ticket';
  return 'task';
}

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * "10:00–11:30"; with the date in front ("24/10 10:00–11:30") when the
 * report is on another day than `refIso` (the report being saved).
 */
export function overlapRange(
  item: Pick<OverlapItem, 'start_time' | 'end_time'>,
  fmtTime: (d: Date) => string,
  fmtDay: (d: Date) => string,
  refIso?: string | null
): string {
  const start = new Date(item.start_time);
  const end = item.end_time ? new Date(item.end_time) : null;
  const sameDay = !refIso || dayKey(new Date(refIso)) === dayKey(start);
  const head = sameDay ? '' : `${fmtDay(start)} `;
  return end ? `${head}${fmtTime(start)}–${fmtTime(end)}` : `${head}${fmtTime(start)}`;
}

/** The overlapping reports that can be opened for editing (logs; a running timer cannot). */
export function editableOverlaps(c: OverlapConflict): OverlapItem[] {
  return c.overlaps.filter((o) => o.kind === 'log');
}

/** The shortened length in whole minutes (for a form that sends minutes); null when not possible. */
export function shortenedMinutes(c: OverlapConflict): number | null {
  if (!c.suggestedEnd || !c.requestedStart) return null;
  const m = Math.floor((new Date(c.suggestedEnd).getTime() - new Date(c.requestedStart).getTime()) / 60_000);
  return m >= 1 ? m : null;
}

/** The times a form sends again when shortening. */
export type ShortenedTimes = { start_time: string; end_time: string; /** D-299: save as asked, the employee worked in parallel */ confirm_parallel?: boolean };

/** The times to send again when shortening: the same start, the suggested end. */
export function shortenedTimes(c: OverlapConflict): ShortenedTimes | null {
  if (!c.suggestedEnd || !c.requestedStart) return null;
  return { start_time: c.requestedStart, end_time: c.suggestedEnd };
}
